import { useCallback, useMemo, useRef } from 'react';
import { getVenue, getVenueIndex } from '../../api/venueApi';
import { getVenueTagList } from '../../api/venueTagApi';
import { VenueFilterData, VenueIndexData } from '../../dto/EntityRepository';
import { VenueIndexView } from '../../enum/EnumRepository';
import { useExhibitionVenueStore } from '../../store/exhibitionVenueStore';
import { getDefaultVenueSortData } from '../../util/venueListParams';
import { useApiWrapper } from './apiWrapper';

let latestVenueListRequestId = 0;
let latestTagListRequestId = 0;
const latestVenueDetailRequestIds = new Map<string, number>();

type VenueListLoadResult = 'loaded' | 'failed' | 'stale';

const advanceVenueDetailRequestId = (venueId: string): number => {
  const requestId = (latestVenueDetailRequestIds.get(venueId) ?? 0) + 1;
  latestVenueDetailRequestIds.set(venueId, requestId);
  return requestId;
};

const normalizeVenueIndex = (venue: VenueIndexData): VenueIndexData => ({
  ...venue,
  id: String(venue.id),
  name: venue.name ?? '',
  nameEn: venue.nameEn ?? '',
});

const fetchAllVenues = async (
  filterData: VenueFilterData,
  view: VenueIndexView,
): Promise<VenueIndexData[]> => {
  const response = await getVenueIndex(getDefaultVenueSortData(), filterData, view);
  return (response.data.venues ?? []).map(normalizeVenueIndex);
};

export const useExhibitionVenueActions = () => {
  const withApiHandler = useApiWrapper();
  const withApiHandlerRef = useRef(withApiHandler);
  withApiHandlerRef.current = withApiHandler;

  const loadVenues = useCallback(async (
    filterData = useExhibitionVenueStore.getState().filterData,
    view = useExhibitionVenueStore.getState().view,
  ): Promise<VenueListLoadResult> => {
    const requestId = ++latestVenueListRequestId;

    const result = await withApiHandlerRef.current(async () => {
      let venues: VenueIndexData[];
      try {
        venues = await fetchAllVenues(filterData, view);
      } catch (error) {
        if (requestId !== latestVenueListRequestId) return 'stale' as const;
        throw error;
      }
      if (requestId !== latestVenueListRequestId) return 'stale' as const;
      useExhibitionVenueStore.getState().setVenueList(venues, view);
      return 'loaded' as const;
    }, ['exhibition-venue-list', view, filterData], {}, false);

    return result ?? 'failed';
  }, []);

  const loadTags = useCallback(async () => {
    const requestId = ++latestTagListRequestId;
    await withApiHandlerRef.current(async () => {
      try {
        const response = await getVenueTagList();
        if (requestId !== latestTagListRequestId) return;
        useExhibitionVenueStore.getState().setTags(response.data);
      } catch (error) {
        if (requestId !== latestTagListRequestId) return;
        throw error;
      }
    }, ['exhibition-venue-tag-list'], {}, false);
  }, []);

  const addInterestExhibitions = useCallback(async (venueId: string): Promise<void> => {
    const normalizedVenueId = venueId.trim();
    if (!normalizedVenueId) return;

    const current = useExhibitionVenueStore.getState();
    if (!current.beginInterestVenueSelection(normalizedVenueId)) return;

    const requestId = advanceVenueDetailRequestId(normalizedVenueId);
    const response = await withApiHandlerRef.current(
      async () => {
        try {
          return await getVenue(normalizedVenueId);
        } catch (error) {
          if (latestVenueDetailRequestIds.get(normalizedVenueId) !== requestId) {
            return undefined;
          }
          throw error;
        }
      },
      ['exhibition-venue-detail', normalizedVenueId],
      {},
      false,
    );
    if (latestVenueDetailRequestIds.get(normalizedVenueId) !== requestId) return;
    if (!response) {
      useExhibitionVenueStore.getState().rejectVenueDetail(normalizedVenueId);
      return;
    }

    useExhibitionVenueStore.getState().resolveVenueDetail(
      normalizedVenueId,
      response.data,
    );
  }, []);

  const selectVenueDetail = useCallback(async (venueId: string): Promise<void> => {
    const normalizedVenueId = venueId.trim();
    if (!normalizedVenueId) return;

    const current = useExhibitionVenueStore.getState();
    if (!current.beginVenueDetailSelection(normalizedVenueId)) return;

    const requestId = advanceVenueDetailRequestId(normalizedVenueId);
    const response = await withApiHandlerRef.current(
      async () => {
        try {
          return await getVenue(normalizedVenueId);
        } catch (error) {
          if (latestVenueDetailRequestIds.get(normalizedVenueId) !== requestId) {
            return undefined;
          }
          throw error;
        }
      },
      ['exhibition-venue-detail', normalizedVenueId],
      {},
      false,
    );
    if (latestVenueDetailRequestIds.get(normalizedVenueId) !== requestId) return;
    if (!response) {
      useExhibitionVenueStore.getState().rejectVenueDetail(normalizedVenueId);
      return;
    }

    useExhibitionVenueStore.getState().resolveVenueDetail(
      normalizedVenueId,
      response.data,
    );
  }, []);

  const clearVenueDetail = useCallback(() => {
    useExhibitionVenueStore.getState().clearVenueDetail();
  }, []);

  const removeInterestVenue = useCallback((venueId: string) => {
    const normalizedVenueId = venueId.trim();
    if (!normalizedVenueId) return;
    const current = useExhibitionVenueStore.getState();
    if (current.selectedVenueId !== normalizedVenueId) {
      advanceVenueDetailRequestId(normalizedVenueId);
    }
    current.removeInterestVenue(normalizedVenueId);
  }, []);

  const setFilterData = useCallback((filterData: VenueFilterData) => {
    useExhibitionVenueStore.getState().setFilterData(filterData);
    void loadVenues(filterData, useExhibitionVenueStore.getState().view);
  }, [loadVenues]);

  const setView = useCallback((view: VenueIndexView) => {
    const current = useExhibitionVenueStore.getState();
    if (current.view === view) return;
    const filterData = current.filterData;
    current.setView(view);
    void loadVenues(filterData, view).then((result) => {
      if (result !== 'failed') return;
      const latest = useExhibitionVenueStore.getState();
      if (latest.view === view && latest.filterData === filterData) {
        latest.setView(latest.loadedView);
        void loadVenues(filterData, latest.loadedView);
      }
    });
  }, [loadVenues]);

  return useMemo(() => ({
    loadVenues,
    loadTags,
    selectVenueDetail,
    clearVenueDetail,
    addInterestExhibitions,
    removeInterestVenue,
    setFilterData,
    setView,
  }), [
    addInterestExhibitions,
    clearVenueDetail,
    loadTags,
    loadVenues,
    removeInterestVenue,
    selectVenueDetail,
    setFilterData,
    setView,
  ]);
};

/** @deprecated 상태는 useExhibitionVenueStore selector로 구독한다. */
export const useExhibitionVenue = () => {
  const store = useExhibitionVenueStore();
  const actions = useExhibitionVenueActions();
  return { ...store, ...actions };
};
